import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { Calendar as LucideCalendar } from 'lucide-react';
import { format } from 'date-fns';
import './DateSelector.css';

const DateSelector = ({ dateRange, setDateRange, startDate, setStartDate, endDate, setEndDate }) => {
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [selectedRange, setSelectedRange] = useState([new Date(), new Date()]);
  const [customRangeText, setCustomRangeText] = useState(localStorage.getItem('customRangeText') || '');


  // Store customRangeText globally
  useEffect(() => {
    localStorage.setItem('customRangeText', customRangeText);
  }, [customRangeText]);

  // Set initial selection from props
  useEffect(() => {
    if (startDate && endDate) {
      setSelectedRange([new Date(startDate), new Date(endDate)]);
    }
  }, [startDate, endDate]);

  const handleDateRangeChange = (range) => {
    const today = new Date();
    let start, end;

    const getNextDay = (date) => {
      const next = new Date(date);
      next.setDate(next.getDate() + 1);
      return next;
    };

    switch (range) {
      case 'today':
        start = format(today, 'yyyy-MM-dd');
        end = format(getNextDay(today), 'yyyy-MM-dd');
        break;
      case 'tomorrow': {
        const tomorrow = getNextDay(today);
        start = format(tomorrow, 'yyyy-MM-dd');
        end = format(getNextDay(tomorrow), 'yyyy-MM-dd');
        break;
      }
      case 'thisWeek': {
        start = format(today, 'yyyy-MM-dd');
        const endOfWeek = new Date(today);
        endOfWeek.setDate(today.getDate() + (7 - today.getDay()));
        end = format(getNextDay(endOfWeek), 'yyyy-MM-dd');
        break;
      }
      case 'thisWeekend': {
        const dayOfWeek = today.getDay(); // 0 = Sunday, 6 = Saturday
        let friday = new Date(today);
        let sunday = new Date(today);

        if (dayOfWeek === 0) {
          friday.setDate(today.getDate() + 5);
          sunday.setDate(today.getDate() + 7);
        } else {
          friday.setDate(today.getDate() + (5 - dayOfWeek));
          sunday.setDate(today.getDate() + (7 - dayOfWeek));
        }

        start = format(friday, 'yyyy-MM-dd');
        end = format(getNextDay(sunday), 'yyyy-MM-dd');
        break;
      }
      case 'thisMonth': {
        start = format(today, 'yyyy-MM-dd');
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        end = format(getNextDay(lastDay), 'yyyy-MM-dd');
        break;
      }
      case 'nextMonth': {
        const nextMonthFirstDay = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        const nextMonthLastDay = new Date(today.getFullYear(), today.getMonth() + 2, 0);

        start = format(nextMonthFirstDay, 'yyyy-MM-dd');
        end = format(getNextDay(nextMonthLastDay), 'yyyy-MM-dd');
        break;
      }
      case 'thisYear': {
        start = format(today, 'yyyy-MM-dd');
        const endOfYear = new Date(today.getFullYear(), 11, 31);
        end = format(getNextDay(endOfYear), 'yyyy-MM-dd');
        break;
      }
      case 'custom':
        setShowCustomRange(true);
        return;
      default:
        return;
    }

    setStartDate(start);
    setEndDate(end);
    setDateRange(range);
    setCustomRangeText('');

    localStorage.setItem('startDate', start); // already a formatted string
    localStorage.setItem('endDate', end);     // already a formatted string
    localStorage.setItem('dateRange', range); // use actual range
  };

  const handleCalendarChange = (dates) => {
    setSelectedRange(dates);
  };

  const formatDate = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const handleApply = () => {
    const [start, end] = selectedRange;
    const nextDay = new Date(end);
    nextDay.setDate(end.getDate() + 1);

    setStartDate(formatDate(start));
    setEndDate(formatDate(nextDay));

    const formattedStart = format(start, 'MMM dd, yyyy').toUpperCase();
    const formattedEnd = format(end, 'MMM dd, yyyy').toUpperCase();
    const formattedRange = `${formattedStart} - ${formattedEnd}`;

    setCustomRangeText(formattedRange);
    setDateRange('custom');
    setShowCustomRange(false);

    localStorage.setItem('startDate', formatDate(start));
    localStorage.setItem('endDate', formatDate(nextDay));
    localStorage.setItem('dateRange', 'custom');
  };

  const handleCancel = () => {
    setCustomRangeText('');
    setDateRange('');
    setShowCustomRange(false);
  };

  return (
    <div className="date-selector">
      <div className="date-dropdown-container">
        <LucideCalendar className="calendar-icon" size={20} />
        <select
          value={dateRange === 'custom' && customRangeText ? '' : dateRange}
          onChange={(e) => {
            const selectedRange = e.target.value;
            setDateRange(selectedRange);
            handleDateRangeChange(selectedRange);
          }}
          className="date-dropdown"
        >
          <option value="" disabled>{customRangeText || 'Select Date Range'}</option>
          <option value="today">Today</option>
          <option value="tomorrow">Tomorrow</option>
          <option value="thisWeek">This Week</option>
          <option value="thisWeekend">This Weekend</option>
          <option value="thisMonth">This Month</option>
          <option value="nextMonth">Next Month</option>
          <option value="custom">Custom Range</option>
        </select>
      </div>

      {showCustomRange && (
        <div className="date-range-picker">
          <Calendar
            selectRange={true}
            value={selectedRange}
            onChange={handleCalendarChange}
            minDate={new Date()}
          />
          <div className="calendar-buttons">
            <button className="apply-btn" onClick={handleApply}>Apply</button>
            <button className="cancel-btn" onClick={handleCancel}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateSelector;
